/**
 * Life4Earth V2 — React hook for loading projects from the API.
 *
 * Replaces: getAllProjectsSeedPlusAdded() + mergeProjectsWithOverrides()
 * Returns projects in the seed-data shape the existing pipeline expects.
 *
 * Includes fallback to seed data if the API is unreachable,
 * so the app keeps working during the transition period.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchProjects, toSeedShape } from "./projects";
import type { ProjectResponse } from "./projects";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

export interface UseApiProjectsResult {
  /** Projects in seed-data shape (project_GUID, project_name, etc.) */
  projects: Record<string, any>[];
  /** Raw API responses (for components that want the new shape) */
  raw: ProjectResponse[];
  /** True while the initial fetch is in progress */
  loading: boolean;
  /** Error message if the API call failed */
  error: string | null;
  /** Whether we're using API data or fell back to seed data */
  source: "api" | "seed" | "loading";
  /** Force a refetch from the API */
  refetch: () => void;
}

export function useApiProjects(
  fallbackFn?: () => any[]
): UseApiProjectsResult {
  const [projects, setProjects] = useState<Record<string, any>[]>([]);
  const [raw, setRaw] = useState<ProjectResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "seed" | "loading">("loading");
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    invalidateAndRefetch(QUERY_KEYS.projects);
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchForKey(
      QUERY_KEYS.projects,
      async () => {
        const apiProjects = await fetchProjects("active");
        return { raw: apiProjects, transformed: apiProjects.map(toSeedShape) };
      },
      {
        fallbackFn: fallbackFn
          ? () => {
              const seedProjects = fallbackFn();
              return { raw: [], transformed: seedProjects };
            }
          : undefined,
      }
    )
      .then((result) => {
        if (!mountedRef.current) return;
        setRaw(result.raw);
        setProjects(result.transformed);
        setSource("api");
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.warn(
          "[useApiProjects] API unreachable, falling back to seed data:",
          err.message
        );
        setError(err.message);
        setSource("seed");
        setLoading(false);
      });
  }, [fallbackFn]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === QUERY_KEYS.projects) {
        load();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [load]);

  return { projects, raw, loading, error, source, refetch };
}
